import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { FileText, Printer, Shield, AlertTriangle, Calculator, ClipboardList, TrendingDown } from "lucide-react";

export default function BoardPackPage() {
  const { activeEntity, entities } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        const { data: d } = await api.get("/reports/board-pack", { params });
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [entityFilter]);

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Generating Board Pack...</div>;
  if (!data) return <div className="stratum-card p-12 text-center text-[#6B7280]">No data available</div>;

  const hs = data.sections.find(s => s.id === "health_score")?.data || {};
  const afs = data.sections.find(s => s.id === "afs_note")?.data || {};
  const anomaly = data.sections.find(s => s.id === "anomaly_summary")?.data || {};
  const recon = data.sections.find(s => s.id === "depreciation_recon")?.data || {};
  const tax = data.sections.find(s => s.id === "tax_summary")?.data?.totals || {};
  const policy = data.sections.find(s => s.id === "policy_summary")?.data || {};

  return (
    <div className="space-y-6" data-testid="board-pack-page">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <FileText size={24} className="inline mr-2 text-[#C8A961]" />
            Board Pack
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Combined report: Health Score + AFS Note + Policy + Anomalies + Tax</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
            <option value="">Group (All)</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={() => window.print()} className="stratum-btn-primary flex items-center gap-2" data-testid="print-board-pack-btn">
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Cover */}
        <div className="stratum-card overflow-hidden">
          <div className="p-8 bg-[#0B1020] text-white text-center rounded-t-lg">
            <img src="/assets/stratum_logomark.png" alt="" className="w-12 h-12 mx-auto mb-3 rounded-xl" />
            <h1 className="font-display text-2xl font-bold">{data.pack.title}</h1>
            <p className="text-white/60 mt-1">{data.pack.company} | {data.pack.period} | {data.pack.currency}</p>
            <p className="text-xs text-white/30 mt-2">Prepared by {data.pack.generated_by} on {data.pack.generated_at?.substring(0, 10)}</p>
          </div>

          {/* 1. Health Score */}
          <div className="p-6 border-b border-[#E8E2D5]">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <Shield size={18} className="text-[#C8A961]" /> 1. Compliance Health Score
            </h2>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <div className={`font-mono text-5xl font-bold ${hs.total_score >= 70 ? "text-[#065F46]" : hs.total_score >= 50 ? "text-[#C8A961]" : "text-[#991B1B]"}`}>{hs.total_score}</div>
                <div className="text-sm text-[#6B7280]">/ 100 (Grade {hs.grade})</div>
              </div>
              <div className="flex-1 grid grid-cols-4 gap-3">
                {hs.components && Object.values(hs.components).map((c, i) => (
                  <div key={i} className="text-center p-2 rounded-lg bg-[#FAF8F4]">
                    <div className="font-mono text-lg font-semibold tabular-nums">{c.score}/{c.max}</div>
                    <div className="text-[10px] text-[#6B7280]">{c.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2. AFS Note */}
          <div className="p-6 border-b border-[#E8E2D5]">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <FileText size={18} className="text-[#C8A961]" /> 2. AFS PPE Note Summary
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-lg font-semibold tabular-nums">{formatCurrency(afs.totals?.closing_cost)}</div>
                <div className="text-xs text-[#6B7280]">Total Cost</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-lg font-semibold tabular-nums text-[#991B1B]">({formatCurrency(Math.abs(afs.totals?.closing_accum || 0))})</div>
                <div className="text-xs text-[#6B7280]">Accum Depreciation</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-lg font-semibold tabular-nums text-[#0B1020]">{formatCurrency(afs.totals?.closing_carrying)}</div>
                <div className="text-xs text-[#6B7280]">Carrying Amount</div>
              </div>
            </div>
            {afs.reconciliation && (
              <table className="w-full text-xs">
                <thead><tr className="bg-[#0B1020] text-white"><th className="px-2 py-1.5 text-left">Category</th><th className="px-2 py-1.5 text-right">Cost</th><th className="px-2 py-1.5 text-right">Accum Dep</th><th className="px-2 py-1.5 text-right">Carrying</th></tr></thead>
                <tbody>
                  {afs.reconciliation.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}>
                      <td className="px-2 py-1">{r.category_name}</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(r.closing_cost)}</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums text-[#991B1B]">({formatCurrency(Math.abs(r.closing_accum))})</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums font-medium">{formatCurrency(r.closing_carrying)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* 3. Anomalies */}
          <div className="p-6 border-b border-[#E8E2D5]">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <AlertTriangle size={18} className="text-[#C8A961]" /> 3. Anomaly & Risk Summary
            </h2>
            <div className="flex gap-4 mb-3">
              <span className="px-3 py-1 rounded-full bg-[#991B1B]/10 text-[#991B1B] text-xs font-medium">{anomaly.critical || 0} Critical</span>
              <span className="px-3 py-1 rounded-full bg-[#92400E]/10 text-[#92400E] text-xs font-medium">{anomaly.warnings || 0} Warnings</span>
              <span className="px-3 py-1 rounded-full bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium">{anomaly.info || 0} Info</span>
            </div>
            {anomaly.top_issues?.length > 0 ? (
              <div className="space-y-1">
                {anomaly.top_issues.slice(0, 5).map((a, i) => (
                  <div key={i} className="text-xs p-2 rounded bg-[#FAF8F4]">
                    <span className="font-mono font-medium">{a.tag}</span> — {a.message}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#065F46]">No anomalies detected. Asset register is clean.</p>
            )}
          </div>

          {/* 4. Dep Recon */}
          <div className="p-6 border-b border-[#E8E2D5]">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <Calculator size={18} className="text-[#C8A961]" /> 4. Depreciation Reconciliation
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-[#FAF8F4]">
                <div className="text-xs text-[#6B7280] mb-1">Per Asset Register</div>
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(recon?.reconciliation?.depreciation_per_asset_register)}</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4]">
                <div className="text-xs text-[#6B7280] mb-1">Per Journals</div>
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(recon?.reconciliation?.depreciation_per_journals)}</div>
              </div>
              <div className={`p-3 rounded-lg ${recon?.reconciliation?.is_reconciled ? "bg-[#065F46]/10" : "bg-[#991B1B]/10"}`}>
                <div className="text-xs text-[#6B7280] mb-1">Variance</div>
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(recon?.reconciliation?.variance)}</div>
              </div>
            </div>
          </div>

          {/* 5. Tax Summary */}
          <div className="p-6 border-b border-[#E8E2D5]">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <TrendingDown size={18} className="text-[#C8A961]" /> 5. Tax Depreciation & Deferred Tax
            </h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(tax.total_acc_annual)}</div>
                <div className="text-[10px] text-[#6B7280]">Acc Dep (Annual)</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(tax.total_tax_annual)}</div>
                <div className="text-[10px] text-[#6B7280]">Tax Dep (Annual)</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(tax.total_temp_diff)}</div>
                <div className="text-[10px] text-[#6B7280]">Temp Difference</div>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF8F4] text-center">
                <div className="font-mono text-base font-semibold tabular-nums">{formatCurrency(tax.total_deferred_tax)}</div>
                <div className="text-[10px] text-[#6B7280]">Deferred Tax @{tax.tax_rate}%</div>
              </div>
            </div>
          </div>

          {/* 6. Policy Summary */}
          <div className="p-6">
            <h2 className="font-display text-lg font-semibold text-[#0B1020] flex items-center gap-2 mb-4">
              <ClipboardList size={18} className="text-[#C8A961]" /> 6. Policy Summary
            </h2>
            <div className="text-sm text-[#6B7280] mb-3">
              {policy.summary?.asset_count || 0} assets | {policy.summary?.categories_with_policy || 0} category policies | {policy.summary?.disposals_count || 0} disposals | {policy.summary?.reassessments_count || 0} reassessments
            </div>
            {policy.category_policies?.length > 0 && (
              <table className="w-full text-xs">
                <thead><tr className="bg-[#FAF8F4]"><th className="px-2 py-1.5 text-left">Category</th><th className="px-2 py-1.5 text-left">Method</th><th className="px-2 py-1.5 text-right">Life (yrs)</th><th className="px-2 py-1.5 text-right">Residual</th><th className="px-2 py-1.5 text-right">Carrying</th></tr></thead>
                <tbody>
                  {policy.category_policies.map((cp, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}>
                      <td className="px-2 py-1">{cp.category}</td>
                      <td className="px-2 py-1">{cp.method}</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{cp.useful_life_years}</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{cp.residual_pct}%</td>
                      <td className="px-2 py-1 text-right font-mono tabular-nums">{formatCurrency(cp.total_carrying)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[#E8E2D5] bg-[#FAF8F4] rounded-b-lg text-center text-xs text-[#6B7280]">
            Generated by Stratum — The Intelligent Asset Register | {data.pack.generated_at?.substring(0, 10)}
          </div>
        </div>
      </div>
    </div>
  );
}
